import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Alert, AlertTitle, Box, Button, Grid, Paper, TextField, Tooltip, Typography } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ClearIcon from '@mui/icons-material/Clear'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import DataTable from '../../Shared/DataTable'
import ExportMenu from '../../Shared/ExportMenu'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { motivosIndisponiveis } from '../../../services/reportServices'
import { mensagemDeErro } from '../../../services/apiClient'
import PermissionComponent from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import useUserNames, { primeUserNames } from '../../../utils/useUserNames'

const MOTIVO_PADRAO = 'Indicador sem origem de dados disponível na API.'

/** Identidade estável: um `[]` novo a cada render reiniciaria a busca de nomes. */
const VAZIO = []

const slug = text =>
  // NFD separa o acento da letra; o filtro seguinte descarta os sinais soltos.
  text
    .normalize('NFD')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')

/**
 * Célula de um indicador que pode não existir.
 *
 * Valor `null` vira "—" com o motivo da API no Tooltip — mesma regra do
 * Dashboard. Nunca um zero no lugar de um número que o banco não sustenta:
 * relatório é exportado e lido meses depois, sem ninguém por perto para
 * explicar que aquele zero significava "não sabemos".
 */
export const ValorOuIndisponivel = ({ value, motivo, format }) => {
  const indisponivel = value === null || value === undefined

  if (!indisponivel) return <>{format ? format(value) : value}</>

  return (
    <Tooltip title={motivo || MOTIVO_PADRAO}>
      <Box component="span" sx={{ color: 'text.disabled', cursor: 'help' }}>
        —
      </Box>
    </Tooltip>
  )
}

/** Totais do recorte inteiro — não da página. */
const ResumoDoRelatorio = ({ fields, summary, motivos }) => {
  if (!fields || fields.length === 0 || !summary) return null

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2}>
        {fields.map(field => (
          <Grid item xs={6} sm={4} md={2} key={field.id}>
            <Typography variant="caption" color="text.secondary" display="block">
              {field.label}
            </Typography>
            <Typography variant="h6" component="div">
              <ValorOuIndisponivel
                value={field.value(summary)}
                motivo={motivos[field.indicator || field.id]}
                format={field.format}
              />
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}

/**
 * Painel genérico dos relatórios: filtros de período/filial, filtros extras
 * definidos pelo relatório, resumo, tabela paginada e exportação.
 *
 * `extraFilters` é uma render prop `(values, setValue) => ReactNode`.
 * `columns` pode ser uma lista ou `(motivos, nomeDe) => lista`; `exportColumns`,
 * uma lista ou `nomeDe => lista`.
 * `fetcher` é `filtros => ({ items, total, summary, notes, unavailable })`.
 *
 * `camposDeUsuario` nomeia os campos da linha que guardam ID de usuário. O
 * painel resolve todos de uma vez e entrega `nomeDe` às colunas: a resolução
 * mora aqui, e não em cada relatório, porque é aqui que as linhas existem —
 * um lote por página em vez de uma busca por coluna.
 */
const ReportPanel = ({
  title,
  description,
  columns,
  fetcher,
  extraFilters,
  exportColumns,
  summaryFields,
  initialExtraValues = {},
  camposDeUsuario = VAZIO,
}) => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()

  const defaultSubCompanyId = subCompany.length === 1 ? subCompany[0].subCompanyId.trim() : ''

  const [subCompanyId, setSubCompanyId] = useState(defaultSubCompanyId)
  const [startDate, setStartDate] = useState('')
  const [finishDate, setFinishDate] = useState('')
  const [extraValues, setExtraValues] = useState(initialExtraValues)

  const [applied, setApplied] = useState({
    subCompanyId: defaultSubCompanyId,
    startDate: '',
    finishDate: '',
    ...initialExtraValues,
  })

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState(null)
  const [notes, setNotes] = useState([])
  const [unavailable, setUnavailable] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const setExtraValue = useCallback(
    (name, value) => setExtraValues(current => ({ ...current, [name]: value })),
    []
  )

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      // A API pagina no banco: `offset` é o registro inicial, não o número da
      // página. São centenas de milhares de linhas — paginar em memória seria
      // trazer tudo para descartar quase tudo.
      const resposta = await fetcher({
        ...applied,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
      setRows(resposta.items)
      setTotal(resposta.total)
      setSummary(resposta.summary)
      setNotes(resposta.notes)
      setUnavailable(resposta.unavailable)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar o relatório.'), 'error')
      setRows([])
      setTotal(0)
      setSummary(null)
      setNotes([])
      setUnavailable([])
    } finally {
      setLoading(false)
    }
  }, [fetcher, applied, page, rowsPerPage, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleApply = () => {
    setPage(0)
    setApplied({ subCompanyId, startDate, finishDate, ...extraValues })
  }

  const handleClear = () => {
    setSubCompanyId(defaultSubCompanyId)
    setStartDate('')
    setFinishDate('')
    setExtraValues(initialExtraValues)
    setPage(0)
    setApplied({
      subCompanyId: defaultSubCompanyId,
      startDate: '',
      finishDate: '',
      ...initialExtraValues,
    })
  }

  const fileName = useMemo(() => `relatorio-${slug(title)}`, [title])

  const motivos = useMemo(() => motivosIndisponiveis(unavailable), [unavailable])

  // Os IDs de usuário da página, resolvidos em nome pelo SSO num lote só.
  const idsDeUsuario = useMemo(
    () => rows.flatMap(row => camposDeUsuario.map(campo => row[campo])).filter(Boolean),
    [rows, camposDeUsuario]
  )
  const { nomeDe } = useUserNames(idsDeUsuario)

  const colunas = useMemo(
    () => (typeof columns === 'function' ? columns(motivos, nomeDe) : columns),
    [columns, motivos, nomeDe]
  )

  const colunasDeExportacao = useMemo(
    () => (typeof exportColumns === 'function' ? exportColumns(nomeDe) : exportColumns),
    [exportColumns, nomeDe]
  )

  // Export leva TODAS as linhas do filtro, não só a página: pagina a API em
  // lotes até somar o total e aquece os nomes de usuário do conjunto inteiro,
  // para as colunas resolverem via `nomeDe` como na tela.
  const fetchAllForExport = useCallback(async () => {
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const resposta = await fetcher({ ...applied, limit: BLOCO, offset })
      const itens = resposta.items || []
      todas.push(...itens)
      const totalDoFiltro = resposta.total || 0
      offset += BLOCO
      if (offset >= totalDoFiltro || itens.length === 0) break
    }
    if (camposDeUsuario.length > 0) {
      await primeUserNames(todas.flatMap(row => camposDeUsuario.map(campo => row[campo])))
    }
    return todas
  }, [fetcher, applied, camposDeUsuario])

  return (
    <Box>
      <PageHeader
        title={title}
        subtitle={description}
        actions={
          <PermissionComponent permissao={PERMISSOES.RELATORIO_EXPORTAR}>
            <ExportMenu
              rows={rows}
              resolveRows={fetchAllForExport}
              columns={colunasDeExportacao}
              fileName={fileName}
              title={title}
              disabled={total === 0}
            />
          </PermissionComponent>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="De"
              type="date"
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Até"
              type="date"
              value={finishDate}
              onChange={event => setFinishDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          {extraFilters && extraFilters(extraValues, setExtraValue)}

          <Grid item xs={12} md="auto">
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={handleApply}
              disabled={loading}
              sx={{ height: 56 }}
              fullWidth
            >
              Aplicar filtros
            </Button>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              disabled={loading}
              sx={{ height: 56 }}
              fullWidth
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <ResumoDoRelatorio fields={summaryFields} summary={summary} motivos={motivos} />

      {/* Ressalvas: o número existe, mas engana sem contexto. Ficam ao lado do
          dado, não escondidas — e são informativas, não um alerta de falha. */}
      {notes.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Sobre estes números</AlertTitle>
          {notes.map(note => (
            <Typography key={note.code} variant="body2" sx={{ mb: 0.5 }}>
              {note.message}
            </Typography>
          ))}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(Number(event.target.value))
          setPage(0)
        }}
        rowKey={(row, index) => row.id || index}
        emptyMessage="Nenhum registro encontrado para os filtros informados."
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default ReportPanel
